import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const MoneyShopGame = ({ gameData, onComplete }) => {
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [selectedCoins, setSelectedCoins] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);

    const currentItem = gameData?.items?.[currentItemIndex];

    const getTotalValue = () => {
        return selectedCoins.reduce((sum, coin) => sum + coin.value, 0);
    };

    const handleCoinClick = (coin) => {
        if (feedback) return;
        triggerHaptic(30);
        setSelectedCoins(prev => [...prev, coin]);
    };

    const handleRemoveCoin = (index) => {
        if (feedback) return;
        triggerHaptic(20);
        setSelectedCoins(prev => prev.filter((_, i) => i !== index));
    };

    const checkAnswer = () => {
        if (feedback || selectedCoins.length === 0) return;
        const total = getTotalValue();
        const isCorrect = Math.abs(total - currentItem.price) < 0.01;

        if (isCorrect) {
            setFeedback('correct');
            const points = gameData.points_per_item || 20;
            const newScore = score + points;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentItemIndex + 1 >= gameData.items.length) {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                } else {
                    setCurrentItemIndex(prev => prev + 1);
                    setSelectedCoins([]);
                    setFeedback(null);
                }
            }, 4000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 2000);
        }
    };

    const clearCoins = () => {
        if (feedback) return;
        triggerHaptic(50);
        setSelectedCoins([]);
    };

    if (!gameData || !currentItem) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-emerald-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">💰</div>
                <h3 className="text-base md:text-3xl font-black text-emerald-900 tracking-tighter uppercase">Parduotuvė atsidaro...</h3>
            </div>
        );
    }

    const totalValue = getTotalValue();
    const remaining = currentItem.price - totalValue;
    const progress = (currentItemIndex / gameData.items.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-emerald-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-emerald-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🛍️
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Pinigų Parduotuvė
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Pelnas</p>
                        <p className="text-sm md:text-2xl font-black text-emerald-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Prekė</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentItemIndex + 1}/{gameData.items.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-emerald-400 via-green-500 to-teal-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            {/* Product Display */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 text-center border-white/40 shadow-2xl bg-white/60 backdrop-blur-xl flex flex-col items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none text-emerald-900">🛍️</div>
                <div className="text-[8px] md:text-[10px] font-black text-emerald-400 uppercase tracking-[0.5em] mb-4 md:mb-10">Ieškoma Prekė</div>
                
                <div className="space-y-4 md:space-y-8 relative z-10 w-full flex flex-col items-center">
                    <div className="text-7xl md:text-[12rem] mb-4 drop-shadow-2xl animate-float">
                        {currentItem.emoji}
                    </div>
                    <div className="space-y-4 flex flex-col items-center">
                        <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter uppercase leading-none">{currentItem.name}</h3>
                        <div className="bg-emerald-600 text-white px-8 py-3 md:px-12 md:py-6 rounded-2xl md:rounded-[3rem] text-4xl md:text-7xl font-black shadow-xl inline-block border-4 md:border-8 border-emerald-400 transform -rotate-3 hover:rotate-0 transition-transform">
                            {currentItem.price.toFixed(2)}€
                        </div>
                    </div>
                </div>
            </div>

            {/* Bank / Vault (Coin Picker) */}
            <div className="premium-glass rounded-2xl md:rounded-[3rem] p-4 md:p-8 shadow-xl border-white/40 bg-white/40 backdrop-blur-md">
                <div className="flex items-center gap-2 mb-6 px-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                    <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Valiutų Saugykla</h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                    {gameData.available_coins.map((coin, index) => (
                        <button
                            key={index}
                            onClick={() => handleCoinClick(coin)}
                            disabled={!!feedback}
                            className="premium-button-3d relative bg-white p-4 md:p-8 rounded-xl md:rounded-[2rem] border-slate-100 flex flex-col items-center justify-center gap-2 group active:scale-95 transition-all disabled:opacity-40"
                        >
                            <div className="text-3xl md:text-5xl group-hover:scale-125 transition-transform duration-500">
                                {coin.emoji}
                            </div>
                            <div className="text-[10px] md:text-sm font-black text-slate-800 uppercase tracking-tighter">{coin.label}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto">
            {/* Immersive Checkout Tray */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/40 bg-slate-900/90 relative overflow-hidden w-full flex flex-col min-h-[350px]">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">📡</div>
                
                <div className="flex justify-between items-center mb-6">
                    <h4 className="text-[8px] md:text-[10px] font-black text-emerald-400 uppercase tracking-[0.4em]">Kasa / Piniginė</h4>
                    <button 
                        onClick={clearCoins} 
                        disabled={selectedCoins.length === 0 || !!feedback}
                        className="text-[8px] md:text-[10px] font-black text-rose-400 uppercase tracking-widest bg-rose-950/40 px-4 py-2 rounded-full border border-rose-900/50 transition-all active:scale-95 disabled:opacity-20"
                    >
                        🗑️ IŠVALYTI
                    </button>
                </div>

                {/* Interactive Coin Tray */}
                <div className="flex-1 bg-slate-800/80 rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 border-2 border-slate-700 shadow-inner mb-6 overflow-y-auto no-scrollbar flex flex-wrap content-start gap-2 md:gap-4 min-h-[150px]">
                    {selectedCoins.length === 0 ? (
                        <div className="w-full h-full flex flex-col items-center justify-center opacity-30 text-center space-y-2 py-8">
                            <div className="text-6xl">👛</div>
                            <p className="text-sm md:text-xl font-black uppercase tracking-tighter italic font-serif text-slate-400">Dėk monetas į kasą...</p>
                        </div>
                    ) : (
                        selectedCoins.map((coin, index) => (
                            <button
                                key={index}
                                onClick={() => handleRemoveCoin(index)}
                                className="h-12 md:h-20 bg-gradient-to-br from-yellow-300 via-amber-400 to-yellow-600 text-slate-900 px-4 md:px-8 rounded-xl md:rounded-2xl font-black border-b-4 md:border-b-8 border-amber-800 flex items-center gap-2 md:gap-4 animate-pop-in active:translate-y-1 active:border-b-0 hover:scale-105 transition-all"
                            >
                                <span className="text-xl md:text-4xl drop-shadow-md">{coin.emoji}</span>
                                <span className="text-xs md:text-xl tracking-tighter">{coin.label}</span>
                            </button>
                        ))
                    )}
                </div>

                {/* Totals HUD */}
                <div className="grid grid-cols-2 gap-2 mb-6">
                    <div className="bg-slate-800 rounded-xl md:rounded-2xl p-3 md:p-6 border-2 border-slate-700 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Kasoje</p>
                        <p className="text-lg md:text-4xl font-black text-white font-mono">{totalValue.toFixed(2)}€</p>
                    </div>
                    <div className={`rounded-xl md:rounded-2xl p-3 md:p-6 border-2 shadow-inner transition-all duration-500 ${
                        remaining > 0 ? 'bg-amber-950/20 border-amber-600' : 
                        remaining < 0 ? 'bg-rose-950/20 border-rose-600' : 
                        'bg-emerald-950/20 border-emerald-600'
                    }`}>
                        <p className="text-[8px] md:text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">
                            {remaining > 0 ? 'Reikia' : remaining < 0 ? 'Viršyta' : 'TIKSLIAI!'}
                        </p>
                        <p className={`text-lg md:text-4xl font-black font-mono ${
                            remaining > 0 ? 'text-amber-400' : 
                            remaining < 0 ? 'text-rose-400 animate-pulse' : 
                            'text-emerald-400'
                        }`}>
                            {Math.abs(remaining).toFixed(2)}€
                        </p>
                    </div>
                </div>

                <button
                    onClick={checkAnswer}
                    disabled={selectedCoins.length === 0 || !!feedback}
                    className={`
                        premium-button-3d w-full py-3 md:py-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl md:rounded-2xl font-black text-sm md:text-3xl transition-all border-b-4 md:border-b-8 border-emerald-800 uppercase tracking-[0.2em]
                        ${selectedCoins.length > 0 && !feedback ? 'scale-105 active:translate-y-1 active:border-b-0' : 'opacity-20'}
                        disabled:cursor-default
                    `}
                >
                    APMOKĖTI ✓
                </button>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🛒' : '👛'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'MOKĖJIMAS PRIIMTAS!' : 'NETIKSLI SUMA!'}
                            </h2>

                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-base md:text-xl font-bold text-slate-700 text-center leading-relaxed">
                                    {feedback === 'correct' ? 'Puiku! Tiksliai apskaičiavai reikiamą monetų sumą. Prekė sėkmingai apmokėta!' : 'Oi! Panašu, kad monetų suma nesutampa su kaina. Peržiūrėk savo skaičiavimus!'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MoneyShopGame;

